/**
 * Company Network Page
 * Generates team report with radar charts for all members of a selected company
 * Requirements 33-38: Department filter, edge/similarity filtering options
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  Paper,
  TextField,
  Stack,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { organizationApi } from '../../admin/api/organizationApi';
import { analysisApi } from '../../analysis/api/analysisApi';
import type { Company, Department } from '../../admin/types/organization.types';
import {
  generateTeamReportHtml,
  openTeamReportWindow,
  downloadTeamReportPdf,
  type MemberScore,
} from '../utils/teamReportGenerator';
import { generateNetworkGraph, type NetworkGraphData, type NetworkNode } from '../utils/networkGraphGenerator';
import { InteractiveNetworkGraph } from './InteractiveNetworkGraph';

export const CompanyNetworkPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [edgeTopPercent, setEdgeTopPercent] = useState<number>(10);
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(70);
  const [loading, setLoading] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<{
    graphData: NetworkGraphData;
    members: MemberScore[];
    companyName: string;
  } | null>(null);
  const [adjustedNodes, setAdjustedNodes] = useState<NetworkNode[] | null>(null);

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await organizationApi.getCompanies();
        setCompanies(data);
      } catch (err) {
        setError('会社一覧の取得に失敗しました');
        console.error('Failed to load companies:', err);
      } finally {
        setLoading(false);
      }
    };
    loadCompanies();
  }, []);

  // Load departments when company is selected (Requirement 33)
  useEffect(() => {
    const loadDepartments = async () => {
      if (!selectedCompanyId) {
        setDepartments([]);
        setSelectedDepartmentId('all');
        return;
      }

      try {
        setLoadingDepartments(true);
        const data = await organizationApi.getDepartmentsByCompany(selectedCompanyId);
        setDepartments(data);
        setSelectedDepartmentId('all');
      } catch (err) {
        console.error('Failed to load departments:', err);
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };
    loadDepartments();
  }, [selectedCompanyId]);

  // Reset preview when filters change
  useEffect(() => {
    setShowPreview(false);
    setPreviewData(null);
    setAdjustedNodes(null);
  }, [selectedCompanyId, selectedDepartmentId, edgeTopPercent, similarityThreshold]);

  // Get members data
  const getMembersData = useCallback(async (): Promise<{ members: MemberScore[]; companyName: string } | null> => {
    if (!selectedCompanyId) {
      setError('会社を選択してください');
      return null;
    }

    const selectedCompany = companies.find(c => c.id === selectedCompanyId);
    if (!selectedCompany) {
      setError('選択した会社が見つかりません');
      return null;
    }

    let members: MemberScore[] = [];

    // Requirement 34: Filter by department if selected
    if (selectedDepartmentId !== 'all') {
      const employees = await organizationApi.getEmployeesByDepartment(selectedDepartmentId);
      
      if (employees.length === 0) {
        setError('選択した部門に社員が登録されていません');
        return null;
      }

      const response = await analysisApi.getCompanyResults(selectedCompanyId);
      
      if (!response.results || response.results.length === 0) {
        setError('分析結果が見つかりません');
        return null;
      }

      const employeeIds = new Set(employees.map(e => e.id));
      const filteredResults = response.results.filter(r => employeeIds.has(r.userId));

      if (filteredResults.length === 0) {
        setError('選択した部門の社員の分析結果が見つかりません');
        return null;
      }

      members = filteredResults.map(result => ({
        name: result.userName || result.userId,
        scores: {
          A: (result.scores.scores.A || 0) / 2,
          B: (result.scores.scores.B || 0) / 2,
          C: (result.scores.scores.C || 0) / 2,
          D: (result.scores.scores.D || 0) / 2,
        },
      }));
    } else {
      const response = await analysisApi.getCompanyResults(selectedCompanyId);
      
      if (!response.results || response.results.length === 0) {
        setError('分析結果が見つかりません');
        return null;
      }

      members = response.results.map(result => ({
        name: result.userName || result.userId,
        scores: {
          A: (result.scores.scores.A || 0) / 2,
          B: (result.scores.scores.B || 0) / 2,
          C: (result.scores.scores.C || 0) / 2,
          D: (result.scores.scores.D || 0) / 2,
        },
      }));
    }

    const selectedDepartment = departments.find(d => d.id === selectedDepartmentId);
    const companyName = selectedDepartmentId !== 'all' && selectedDepartment
      ? `${selectedCompany.name} - ${selectedDepartment.name}`
      : selectedCompany.name;

    return { members, companyName };
  }, [selectedCompanyId, selectedDepartmentId, companies, departments]);

  // Generate preview
  const handlePreview = useCallback(async () => {
    try {
      setGenerating(true);
      setError(null);

      const data = await getMembersData();
      if (!data) return;

      const graphData = generateNetworkGraph(data.members, 600, 480, {
        edgeTopPercent,
        similarityThreshold: similarityThreshold / 100,
      });

      setPreviewData({
        graphData,
        members: data.members,
        companyName: data.companyName,
      });
      setAdjustedNodes(null);
      setShowPreview(true);
    } catch (err) {
      setError('プレビューの生成に失敗しました');
      console.error('Failed to generate preview:', err);
    } finally {
      setGenerating(false);
    }
  }, [getMembersData, edgeTopPercent, similarityThreshold]);

  // Handle node position changes
  const handlePositionsChange = useCallback((nodes: NetworkNode[]) => {
    setAdjustedNodes(nodes);
  }, []);

  // Build the report HTML (shared by the print-tab flow and the PDF download flow)
  const buildReportHtml = useCallback(async (): Promise<{ html: string; dateStr: string } | null> => {
    if (!selectedCompanyId) {
      setError('会社を選択してください');
      return null;
    }

    const selectedCompany = companies.find(c => c.id === selectedCompanyId);
    if (!selectedCompany) {
      setError('選択した会社が見つかりません');
      return null;
    }

    // Use preview data if available, otherwise fetch fresh
    let members: MemberScore[];
    let reportTitle: string;

    if (previewData) {
      members = previewData.members;
      reportTitle = previewData.companyName;
    } else {
      const data = await getMembersData();
      if (!data) return null;
      members = data.members;
      reportTitle = data.companyName;
    }

    const today = new Date();
    const dateStr = today.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\//g, '.');

    const html = await generateTeamReportHtml({
      companyName: reportTitle,
      date: dateStr,
      members,
      networkOptions: {
        edgeTopPercent,
        similarityThreshold: similarityThreshold / 100,
      },
      // Pass adjusted node positions if available
      adjustedNodePositions: adjustedNodes ?
        adjustedNodes.reduce((acc, node) => {
          acc[node.id] = { x: node.x, y: node.y };
          return acc;
        }, {} as { [id: number]: { x: number; y: number } }) :
        undefined,
    });

    return { html, dateStr };
  }, [selectedCompanyId, companies, previewData, getMembersData, edgeTopPercent, similarityThreshold, adjustedNodes]);

  // Generate report (opens in a new tab for browser printing)
  const handleGenerateReport = useCallback(async () => {
    try {
      setGenerating(true);
      setError(null);
      const built = await buildReportHtml();
      if (!built) return;
      // Open in new window
      openTeamReportWindow(built.html, `${built.dateStr} 組織診断結果`);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'レポートの生成に失敗しました');
      console.error('Failed to generate report:', err);
    } finally {
      setGenerating(false);
    }
  }, [buildReportHtml]);

  // Download report directly as an A4-landscape PDF (deterministic orientation, no print dialog)
  const handleDownloadPdf = useCallback(async () => {
    try {
      setDownloadingPdf(true);
      setError(null);
      const built = await buildReportHtml();
      if (!built) return;
      await downloadTeamReportPdf(built.html, `${built.dateStr} 組織診断結果.pdf`);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'PDFの生成に失敗しました');
      console.error('Failed to download PDF:', err);
    } finally {
      setDownloadingPdf(false);
    }
  }, [buildReportHtml]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          組織分析
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          会社を選択して、組織メンバー全員のレーダーチャート一覧レポートを生成します。
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Company Select (Requirement 33) */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="company-select-label">会社を選択</InputLabel>
          <Select
            labelId="company-select-label"
            value={selectedCompanyId}
            label="会社を選択"
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            disabled={generating}
          >
            {companies.length === 0 ? (
              <MenuItem value="" disabled>
                会社が登録されていません
              </MenuItem>
            ) : (
              companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Department Select (Requirements 33-34) */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="department-select-label">部門を選択</InputLabel>
          <Select
            labelId="department-select-label"
            value={selectedDepartmentId}
            label="部門を選択"
            onChange={(e) => setSelectedDepartmentId(e.target.value)}
            disabled={generating || !selectedCompanyId || loadingDepartments}
          >
            <MenuItem value="all">全て</MenuItem>
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtering Options (Requirements 35-36) */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          ネットワーク図フィルタリング設定
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            type="number"
            label="エッジ上位 (%)"
            value={edgeTopPercent}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= 1 && val <= 100) {
                setEdgeTopPercent(val);
              }
            }}
            slotProps={{ htmlInput: { min: 1, max: 100 } }}
            helperText="上位X%のエッジのみ表示"
            disabled={generating}
            size="small"
          />
          <TextField
            fullWidth
            type="number"
            label="類似度閾値 (%)"
            value={similarityThreshold}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= 0 && val <= 100) {
                setSimilarityThreshold(val);
              }
            }}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
            helperText="類似度X%以上のみ表示"
            disabled={generating}
            size="small"
          />
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
            onClick={handlePreview}
            disabled={!selectedCompanyId || generating}
          >
            {generating ? '読み込み中...' : 'プレビュー'}
          </Button>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AssessmentIcon />}
            onClick={handleGenerateReport}
            disabled={!selectedCompanyId || generating || downloadingPdf}
          >
            {generating ? 'レポート生成中...' : 'レポート作成'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
            startIcon={downloadingPdf ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadPdf}
            disabled={!selectedCompanyId || generating || downloadingPdf}
          >
            {downloadingPdf ? 'PDF生成中...' : 'PDFダウンロード'}
          </Button>
        </Stack>

        {/* Interactive Preview */}
        {showPreview && previewData && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              ネットワーク図プレビュー
            </Typography>
            <InteractiveNetworkGraph
              graphData={previewData.graphData}
              width={600}
              height={480}
              storageKey={`network-positions-${selectedCompanyId}-${selectedDepartmentId}`}
              onPositionsChange={handlePositionsChange}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CompanyNetworkPage;
